import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import * as React from 'react';
import { POINTER_HOVER_SELECTOR, State } from '../../util/interactiveState.js';
import { Box } from '../Box/Box.js';
import { Icon } from '../Icon/Icon.js';
import { Link as LinkMemoized } from '../Link/Link.js';
import { LinkStyled } from '../Link/Link.styled.js';
import '../Tooltip/index.js';
import { Typography } from '../Typography/Typography.js';
import { TypographyStyled } from '../Typography/Typography.styled.js';
import { useThemeContext } from '../../contexts/session/Theme.js';
import { isStaticResource, resources } from '../../static/resources.js';
import styled from 'styled-components';
import textLinkInteractiveState from './textLinkInteractiveState.js';
import { Tooltip } from '../Tooltip/Tooltip.js';

var _excluded = ["hierarchy", "leadingIcon", "trailingIcon", "textVariant", "href", "onClick", "rel", "target", "tabIndex", "label", "tooltipText", "textDecoration"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var textColorToken = {
  primary: 'product.primary',
  secondary: 'deprecated.secondary.interactive',
  tertiary: 'base.subtle'
};
var underlineStyles = {
  textDecoration: 'underline',
  textUnderlineOffset: '4px',
  textUnderlineColor: textColorToken
};
var hoverState = function hoverState() {
  return _defineProperty({}, "".concat(POINTER_HOVER_SELECTOR), {
    '&:hover': underlineStyles
  });
};
var baseTextLinkStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  textDecoration: 'none',
  textUnderlineOffset: '4px'
};
var TextStyled = /*#__PURE__*/styled(Typography).withConfig({
  displayName: "TextLink__TextStyled",
  componentId: "sc-upxc4y-0"
})(_objectSpread(_objectSpread({}, baseTextLinkStyle), {}, {
  fontWeight: 600 // TODO: ADSDEV-300 semibold, change to use token font-weight when available
}));
var TextLinkStyled = /*#__PURE__*/styled.a.withConfig({
  displayName: "TextLink__TextLinkStyled",
  componentId: "sc-upxc4y-1"
})(function (props) {
  return _objectSpread(_defineProperty(_defineProperty({
    display: 'inline-flex',
    textDecoration: 'none',
    pointerEvents: 'initial'
  }, "&".concat(State.Hover, " ").concat(TypographyStyled), hoverState()), "&".concat(State.Hover, " ").concat(LinkStyled), {
    cursor: 'pointer'
  }), props.$interactiveStyle);
});
var TextLinkButtonStyled = /*#__PURE__*/styled(LinkMemoized).withConfig({
  displayName: "TextLink__TextLinkButtonStyled",
  componentId: "sc-upxc4y-2"
})(function (props) {
  return _objectSpread(_defineProperty(_defineProperty({
    display: 'inline-flex',
    color: 'inherit',
    textUnderlineOffset: '4px'
  }, "&".concat(State.Hover, " ").concat(TypographyStyled), hoverState()), "&".concat(State.Hover, " ").concat(LinkStyled), {
    cursor: 'pointer'
  }), props.$interactiveStyle);
});

/**
 * TextLink allows users to navigate to different pages/information. It can appear in-line within other text or as standalone actions.
 */
var TextLink = /*#__PURE__*/React.forwardRef(function (_ref2, ref) {
  var hierarchy = _ref2.hierarchy,
    propLeadingIcon = _ref2.leadingIcon,
    propTrailingIcon = _ref2.trailingIcon,
    textVariant = _ref2.textVariant,
    href = _ref2.href,
    onClick = _ref2.onClick,
    rel = _ref2.rel,
    target = _ref2.target,
    tabIndex = _ref2.tabIndex,
    label = _ref2.label,
    tooltipText = _ref2.tooltipText,
    textDecoration = _ref2.textDecoration,
    rest = _objectWithoutProperties(_ref2, _excluded);
  var textColor = textColorToken[hierarchy || 'primary'];
  var theme = useThemeContext();
  var leadingIcon;
  if (propLeadingIcon) {
    leadingIcon = isStaticResource(propLeadingIcon) ? /*#__PURE__*/React.createElement(Box, {
      display: "inline-flex",
      alignItems: "center",
      me: "4"
    }, resources[propLeadingIcon]) : /*#__PURE__*/React.createElement(Icon, {
      id: propLeadingIcon,
      size: "16",
      me: "4",
      color: textColor
    });
  }
  var isEnforceTooltip = target === '_blank' && tooltipText;
  var effectiveTrailingIcon = isEnforceTooltip ? 'fill.arrow.newpage' : propTrailingIcon;
  var trailingIcon;
  if (effectiveTrailingIcon) {
    trailingIcon = isStaticResource(effectiveTrailingIcon) ? /*#__PURE__*/React.createElement(Box, {
      display: "inline-flex",
      alignItems: "center",
      ms: "4"
    }, resources[effectiveTrailingIcon]) : /*#__PURE__*/React.createElement(Icon, {
      id: effectiveTrailingIcon,
      size: "16",
      ms: "4",
      color: textColor
    });
  }
  var content = /*#__PURE__*/React.createElement(TextStyled, {
    display: "inline-flex",
    textUnderlineOffset: "4px",
    textDecoration: "none",
    fontWeight: 600,
    label: label,
    tag: "span",
    variant: textVariant,
    textColor: textColor
  }, leadingIcon, label, trailingIcon);
  var interactiveStateStyle = textLinkInteractiveState(theme, textColor);
  var textLinkProps = _objectSpread({
    href: href,
    rel: rel,
    target: target,
    tabIndex: tabIndex,
    $interactiveStyle: interactiveStateStyle,
    $textDecoration: textDecoration,
    ref: ref,
    onClick: onClick
  }, rest);
  if (href) {
    var linkElement = /*#__PURE__*/React.createElement(TextLinkStyled, textLinkProps, content);
    return isEnforceTooltip ? /*#__PURE__*/React.createElement(Tooltip, {
      title: tooltipText
    }, linkElement) : linkElement;
  }
  var textLinkButtonProps = {
    onClick: onClick,
    tabIndex: tabIndex,
    $interactiveStyle: interactiveStateStyle,
    $textDecoration: textDecoration,
    ref: ref
  };
  return /*#__PURE__*/React.createElement(TextLinkButtonStyled, _extends({}, textLinkButtonProps, rest), content);
});
TextLink.displayName = 'TextLink';
TextLink.defaultProps = {
  hierarchy: 'primary'
};

export { TextLink, hoverState };
//# sourceMappingURL=TextLink.js.map
